/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { client } from '../../lib/amplify-client';

/**
 * Pack inventory hooks (Phase B — PR B3, Task 9).
 *
 * `useGetPackInventory` fetches the user's unopened pack inventory.
 * `useOpenPackMutation` opens a pack from inventory, creating a new order.
 */

export interface PackInventoryEntry {
  pack_id: string;
  pack_name: string;
  set_slug: string;
  quantity: number;
  pack_art_url?: string | null;
  /** 'gift' when this entry came from a claimed gift; otherwise absent/undefined. */
  source?: string | null;
  /** Sender display name — only present when source === 'gift'. */
  gift_sender_name?: string | null;
  /** Gift ID — only present when source === 'gift'. */
  gift_id?: string | null;
}

export function useGetPackInventory(enabled: boolean) {
  return useQuery({
    queryKey: ['pack-inventory'] as const,
    queryFn: async () => {
      const result = await (client.queries as any).getPackInventory(
        {},
        { authMode: 'userPool' },
      );
      if (result.errors?.length) throw new Error(result.errors[0].message);
      return (result.data ?? []) as PackInventoryEntry[];
    },
    enabled,
    staleTime: 30_000,
  });
}

export function useOpenPackMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (packId: string) => {
      const result = await (client.mutations as any).openPack(
        { pack_id: packId },
        { authMode: 'userPool' },
      );
      if (result.errors?.length) throw new Error(result.errors[0].message);
      return result.data as { order_id: string };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pack-inventory'] });
    },
  });
}
